import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create form data for FormSubmit
      const form = new FormData();
      form.append('name', contactForm.name);
      form.append('email', contactForm.email);
      form.append('subject', contactForm.subject);
      form.append('message', contactForm.message);
      form.append('_subject', `Contact Form: ${contactForm.subject}`);
      form.append('_template', 'box');
      form.append('_captcha', 'false');
      
      const response = await fetch('https://formsubmit.co/felixaz@live.com', {
        method: 'POST',
        body: form
      });
      
      if (response.ok) {
        toast.success('Message sent! I\'ll get back to you soon.');
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'felixaz@live.com' },
    { icon: Phone, label: 'Phone', value: '770-710-2650' },
    { icon: MapPin, label: 'Location', value: 'Greater Nashville Area' }
  ];

  return (
    <section id="contact" className="py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Have questions? Want to learn more? I'd love to hear from you.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-12 items-stretch">
          {/* Contact Information */}
          <div className="flex flex-col">
            <h3 className="text-2xl font-semibold text-white mb-6">Contact Information</h3>
            <div className="space-y-6 mb-8">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="p-3 bg-indigo-500/10 rounded-lg">
                      <Icon className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">{info.label}</p>
                      <p className="text-lg text-white font-medium">{info.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Small Map */}
            <Card className="bg-slate-900 border-indigo-500/20 overflow-hidden flex-grow">
              <div className="h-full min-h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=1020+Meeks+Cemetery+Rd+Burns+TN+37029&zoom=15"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Studio Location"
                ></iframe>
              </div>
              <div className="p-4 bg-slate-900">
                <p className="text-sm text-slate-400">1020 Meeks Cemetery Rd, Burns, TN 37029</p>
              </div>
            </Card>
          </div>
          
          {/* Contact Form */}
          <div className="flex flex-col">
            <Card className="bg-slate-900 border-indigo-500/20 flex-grow flex flex-col">
              <CardContent className="p-6 flex-grow flex flex-col">
                <form onSubmit={handleContactSubmit} className="space-y-6 flex-grow flex flex-col">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className="text-slate-300">Name</Label>
                    <Input 
                      id="contact-name"
                      value={contactForm.name}
                      onChange={(e) => handleContactChange('name', e.target.value)}
                      required
                      className="bg-slate-950 border-slate-700 text-white focus:border-indigo-500"
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-slate-300">Email</Label>
                    <Input 
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => handleContactChange('email', e.target.value)}
                      required
                      className="bg-slate-950 border-slate-700 text-white focus:border-indigo-500"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact-subject" className="text-slate-300">Subject</Label>
                    <Input 
                      id="contact-subject"
                      value={contactForm.subject}
                      onChange={(e) => handleContactChange('subject', e.target.value)}
                      required
                      className="bg-slate-950 border-slate-700 text-white focus:border-indigo-500"
                      placeholder="How can I help?"
                    />
                  </div>
                  
                  <div className="space-y-2 flex-grow flex flex-col">
                    <Label htmlFor="contact-message" className="text-slate-300">Message</Label>
                    <Textarea 
                      id="contact-message"
                      value={contactForm.message}
                      onChange={(e) => handleContactChange('message', e.target.value)}
                      required
                      className="bg-slate-950 border-slate-700 text-white focus:border-indigo-500 flex-grow resize-none"
                      placeholder="Your message..."
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white transition-all hover:scale-105 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <Send className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;